// `evidence/source` — the personalised suggestion starters read, carved from
// `src/hooks/query/useQueryPersonalizedEvidenceSuggestions.ts` (now a bodied
// delegate). GRO-2896.
//
// CLAIMED DESPITE A `/chat/*` ROUTE, and that is a ruling this file has to
// carry rather than assume. The route is `GET /chat/personalized-suggestions`,
// which by itself looks like the case that kept `useBlockedDomains` out of this
// lane (`GET /chat/blocked-domains`, key `chat_blocked_domains`). The difference
// is the CACHE IDENTITY: that read declares a chat identity, this one declares
// `['personalized-evidence-suggestions']`. The EN1 resource ruling was made on
// BOTH prongs agreeing; where they disagree the identity decides, because the
// identity is what the frozen-key law protects and what an invalidation blast
// radius is measured in. `./useQuerySources.ts` was the mirror case —
// `/chat/sources` AND `chat_sources`, both prongs chat — and it was withheld
// for exactly that reason until the program owner's chat adjudication moved it
// on consumer ownership instead. The instrument above is untouched by that:
// this read is claimed on its identity, that one on a ruling, and neither
// argument was used to reach the other.
//
// S1: transport moved to `../api/sources` on the `@heidi/api/client` registry.
//
// S-enabled (cohort S2): the pre-move gate was `!!token && enabled`, with the
// caller's `enabled` DEFAULTING TO FALSE. The token is the only app term and
// collapses into the delegate's boolean; the default stays at the delegate,
// where the caller-facing signature is, rather than being re-declared here.
//
// THE THREE `refetchOn*: false` FLAGS PLUS `staleTime: Infinity` ARE THE
// CONTRACT, not tuning. This query is fetched imperatively: sessionless Evidence
// awareness calls `refetch()` and invalidates the key after each completed
// impression. Restoring any react-query default here would make the payload
// refresh on remount and window focus, which is a different impression count.
import { useQuery } from '@tanstack/react-query';

import {
  PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY,
  type PersonalizedEvidenceSuggestionsResponse,
} from '../../contracts';
import { getPersonalizedEvidenceSuggestions } from '../api/sources';

export interface PersonalizedEvidenceSuggestionsReadOptions {
  /**
   * App-resolved gate, already reduced to one decision — the caller's own
   * `enabled` argument (default `false` at the delegate) AND-ed with the auth
   * token.
   */
  enabled: boolean;
}

export function useQueryPersonalizedEvidenceSuggestions({
  enabled,
}: PersonalizedEvidenceSuggestionsReadOptions) {
  return useQuery<PersonalizedEvidenceSuggestionsResponse>({
    queryKey: PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY,
    queryFn: getPersonalizedEvidenceSuggestions,
    enabled,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
